import React, { useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Button } from "@/components/ui/button";
import { Mail, ArrowRight, CheckCircle, Star, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { addToMarketingList } from "@/services/brevoService";

const NewsletterSubscription = () => {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { trackEvent } = useAnalytics();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newsletterEmail || !/^\S+@\S+\.\S+$/.test(newsletterEmail)) {
      toast.error("Virheellinen sähköpostiosoite", {
        description: "Ole hyvä ja anna kelvollinen sähköpostiosoite."
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { data: checkResult, error: checkError } = await supabase
        .rpc('check_marketing_contact_exists', { p_email: newsletterEmail });
        
      if (checkError) {
        console.error('Error checking if contact exists:', checkError);
        throw new Error('Tarkistuksessa tapahtui virhe. Yritä myöhemmin uudelleen.');
      }
      
      const defaultName = newsletterEmail.split('@')[0];
      console.log(`Subscribing to newsletter: ${newsletterEmail}, using name: ${defaultName}`);
      
      if (checkResult) {
        const { error: updateError } = await supabase.rpc('update_marketing_contact', {
          p_email: newsletterEmail,
          p_name: defaultName,
          p_marketing_consent: true
        });
        
        if (updateError) {
          console.error('Error updating subscription:', updateError);
          throw new Error('Tilaustietojen päivitys epäonnistui');
        }
        
        toast.success("Tilaustietosi päivitetty!", {
          description: "Tilaustietosi on päivitetty onnistuneesti."
        });
      } else {
        const { error: insertError } = await supabase.rpc('insert_marketing_contact', {
          p_email: newsletterEmail,
          p_name: defaultName,
          p_marketing_consent: true,
          p_source: 'article_newsletter'
        });
        
        if (insertError) {
          console.error('Error inserting new subscription:', insertError);
          
          if (insertError.code === '23505' && insertError.message.includes('already exists')) {
            const { error: updateError } = await supabase.rpc('update_marketing_contact', {
              p_email: newsletterEmail,
              p_name: defaultName,
              p_marketing_consent: true
            });
            
            if (updateError) {
              throw new Error('Tilaustietojen päivitys epäonnistui');
            }
            
            toast.success("Tilaustietosi päivitetty!", {
              description: "Tilaustietosi on päivitetty onnistuneesti."
            });
          } else {
            throw new Error('Tilaus epäonnistui. Yritä myöhemmin uudelleen.');
          }
        } else {
          toast.success("Kiitos tilauksestasi!", {
            description: "Olet nyt tilannut uutiskirjeemme."
          });
        }
      }
      
      console.log(`Newsletter subscription parameters - Email: ${newsletterEmail}, Name: ${defaultName}, Source: article_newsletter`);
      
      const result = await addToMarketingList(
        newsletterEmail,
        defaultName,
        'article_newsletter',
        true
      );
      
      if (!result.success) {
        console.warn('Brevo sync warning: Failed to sync with Brevo but subscription was saved locally');
      }
      
      setIsSuccess(true);
      trackEvent('newsletter_subscription_success', { source: 'article_page' });
      setNewsletterEmail("");
      
      try {
        console.log(`Scheduling welcome email series for ${newsletterEmail}`);
        
        const welcomeResult = await supabase.functions.invoke('welcome-email-series', {
          body: { 
            email: newsletterEmail, 
            name: defaultName,
            emailIndex: 0
          }
        });
        
        console.log(`Welcome email immediate result:`, welcomeResult);
        
        const secondEmailDate = new Date();
        secondEmailDate.setHours(secondEmailDate.getHours() + 24);
        
        localStorage.setItem(`welcome_email_next_${newsletterEmail}_2`, JSON.stringify({
          nextIndex: 1,
          scheduledDate: secondEmailDate.toISOString(),
          name: defaultName
        }));
        
        const thirdEmailDate = new Date();
        thirdEmailDate.setHours(thirdEmailDate.getHours() + 96);
        
        localStorage.setItem(`welcome_email_next_${newsletterEmail}_3`, JSON.stringify({
          nextIndex: 2,
          scheduledDate: thirdEmailDate.toISOString(),
          name: defaultName
        }));
        
        console.log(`Welcome email series scheduled: Email 1 immediately, Email 2 in 24 hours, Email 3 in 96 hours`);
      } catch (welcomeError) {
        console.error('Error scheduling welcome email series:', welcomeError);
      }
      
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
      
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      
      toast.error("Tilaus epäonnistui", {
        description: error instanceof Error ? error.message : "Uutiskirjeen tilauksessa tapahtui virhe. Yritä myöhemmin uudelleen."
      });
      
      trackEvent('newsletter_subscription_error', { 
        source: 'article_page',
        error: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  React.useEffect(() => {
    const checkScheduledEmails = async () => {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('welcome_email_next_')) {
          try {
            const emailData = JSON.parse(localStorage.getItem(key) || '');
            const now = new Date();
            const scheduledDate = new Date(emailData.scheduledDate);
            
            if (scheduledDate <= now) {
              console.log(`Time to send scheduled email: ${key}`);
              const emailParts = key.split('_');
              const email = emailParts.slice(3, -1).join('_') || emailParts[3];
              
              const result = await supabase.functions.invoke('welcome-email-series', {
                body: { 
                  email, 
                  name: emailData.name,
                  emailIndex: emailData.nextIndex
                }
              });
              
              console.log(`Scheduled email sent result:`, result);
              
              localStorage.removeItem(key);
            }
          } catch (err) {
            console.error('Error processing scheduled email:', err);
          }
        }
      }
    };
    
    checkScheduledEmails();
    const interval = setInterval(checkScheduledEmails, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div 
      className="bg-gradient-to-r from-indigo-100 to-blue-200 p-6 rounded-xl shadow-sm overflow-hidden relative h-full flex flex-col justify-between max-w-md mx-auto"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <div className="absolute top-0 right-0 transform translate-x-1/4 -translate-y-1/4 opacity-20">
        <Mail size={100} className="text-blue-400" />
      </div>
      <div className="absolute bottom-0 left-0 transform -translate-x-1/4 translate-y-1/4 opacity-10">
        <Star size={80} className="text-indigo-300" />
      </div>
      <div className="absolute -bottom-10 -right-10 opacity-10">
        <Sparkles size={120} className="text-blue-300" />
      </div>
      
      <div className="mb-4 relative z-10">
        <h2 className="text-xl md:text-2xl font-bold text-indigo-800 mb-3">Tilaa uutiskirjeemme</h2>
        <p className="text-indigo-700 text-sm mb-4">Tilaa uutiskirjeemme ja saat tuoreimmat artikkelit, parhaat rahoitusvinkit ja asiantuntijoiden neuvoja suoraan sähköpostiisi.</p>
      </div>
        
      {isSuccess ? (
        <motion.div 
          className="bg-gradient-to-r from-green-100/90 to-emerald-200/90 backdrop-blur-md p-4 rounded-lg text-center mx-auto mb-2 relative z-10"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-600" />
          <h3 className="text-lg font-semibold mb-1 text-green-700">Kiitos tilauksestasi!</h3>
          <p className="text-green-600 text-sm">Olet nyt tilannut uutiskirjeemme.</p>
        </motion.div>
      ) : (
        <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-2 w-full relative z-10">
          <input 
            type="email" 
            placeholder="Sähköpostiosoitteesi" 
            className="px-3 py-2 rounded-lg w-full text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 border border-indigo-100 shadow-inner text-sm"
            aria-label="Sähköpostiosoitteesi"
            value={newsletterEmail}
            onChange={(e) => setNewsletterEmail(e.target.value)}
            required
          />
          <Button 
            type="submit"
            className="bg-gradient-to-r from-indigo-500 to-blue-600 hover:opacity-90 text-white px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap shadow-sm text-sm w-full"
            aria-label="Tilaa uutiskirje"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              "Tilataan..."
            ) : (
              <>
                Tilaa nyt
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      )}
    </motion.div>
  );
};

export default NewsletterSubscription;
